import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	ArrowUpRight,
	Briefcase,
	FileText,
	Mail,
	Phone,
	PlusSquare,
	Search,
	Users,
} from 'react-feather';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import BncLogoSpinner from '../components/BncLogoSpinner';
import ClientDetails from '../components/clients/ClientDetails';
import OrderDetails from '../components/OrderDetails';
import ClientHubCreateClientPanel from '../components/clientHub/ClientHubCreateClientPanel';
import ClientHubOrderComposer from '../components/clientHub/ClientHubOrderComposer';
import { fetchClients, fetchClientById } from '../slices/clientSlice';
import { fetchOrderById, fetchOrders } from '../slices/ordersSlice';
import { fetchSelf } from '../slices/userSlice';
import api from '../config/api';
import { formatPhoneNumber, removePhoneFormatting } from '../hooks/phoneFormat';
import { getLatestContractSentEvent } from '../utils/contractTracking';

const ORDER_STATUS_LABELS = {
	'-1': 'Canceled',
	1: 'Quote',
	2: 'Order Set',
	3: 'Permits',
	4: 'Invoice Sent',
	5: 'Installation',
	6: 'Repair',
	7: 'Review',
	10: 'Done',
};

const CLIENT_STATUS_LABELS = {
	1: 'Waiting',
	2: 'Ok',
	3: 'Blocked',
};

const currencyFormatter = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
	maximumFractionDigits: 0,
});

function getTimeValue(value) {
	if (!value) {
		return 0;
	}

	const parsed = new Date(value).getTime();
	return Number.isNaN(parsed) ? 0 : parsed;
}

function getClientDisplayName(client) {
	return `${client?.firstName || ''} ${client?.lastName || ''}`.trim() || 'Unnamed client';
}

function getClientAddressField(client, field) {
	if (client?.address && typeof client.address === 'object') {
		return client.address[field] || '';
	}

	return client?.[field] || '';
}

function getClientAddressLine(client) {
	const parts = [
		getClientAddressField(client, 'address'),
		getClientAddressField(client, 'city'),
		getClientAddressField(client, 'state'),
		getClientAddressField(client, 'postalCode'),
	].filter(Boolean);

	return parts.join(', ');
}

function getRecordType(order) {
	return Number(order?.status) === 1 ? 'Lead' : 'Order';
}

function getRecordStatusLabel(status) {
	return ORDER_STATUS_LABELS[status] || 'Status';
}

function getRecordAmount(order) {
	const rawValue =
		order?.totalAmount ??
		order?.total ??
		order?.metadata?.totalPrice ??
		order?.metadata?.priceSummary?.estimatedTotal ??
		0;
	const numericValue = Number(rawValue);

	return Number.isFinite(numericValue) ? numericValue : 0;
}

function getRecordActivityTime(order) {
	return Math.max(getTimeValue(order?.updatedAt), getTimeValue(order?.createdAt));
}

function formatDateTime(value) {
	if (!value) {
		return 'Unknown date';
	}

	const parsed = new Date(value);

	if (Number.isNaN(parsed.getTime())) {
		return value;
	}

	return parsed.toLocaleString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
	});
}

function getClientActivityTime(client, relatedOrders) {
	const clientUpdated = Math.max(
		getTimeValue(client?.updatedAt),
		getTimeValue(client?.createdAt)
	);
	const orderUpdated = relatedOrders.reduce(
		(maxValue, order) => Math.max(maxValue, getRecordActivityTime(order)),
		0
	);
	const noteUpdated = (client?.notes || []).reduce(
		(maxValue, note) =>
			Math.max(maxValue, getTimeValue(note?.createdAt || note?.CreatedAt)),
		0
	);

	return Math.max(clientUpdated, orderUpdated, noteUpdated);
}

export default function ClientManagementHub() {
	const dispatch = useDispatch();
	const clients = useSelector((state) => state.clients.items ?? []);
	const selectedClientDetails = useSelector((state) => state.clients.clientById);
	const dashboardOrders = useSelector((state) => state.orders.items ?? []);
	const currentOrder = useSelector((state) => state.orders.order);
	const currentUser = useSelector((state) => state.auth.self);

	const [isBootstrapping, setIsBootstrapping] = useState(true);
	const [searchQuery, setSearchQuery] = useState('');
	const [recordFilter, setRecordFilter] = useState('all');
	const [selectedClientId, setSelectedClientId] = useState(null);
	const [clientRecords, setClientRecords] = useState([]);
	const [isLoadingClientRecords, setIsLoadingClientRecords] = useState(false);
	const [clientRecordsError, setClientRecordsError] = useState('');
	const [showCreateClientPanel, setShowCreateClientPanel] = useState(false);
	const [showClientDetails, setShowClientDetails] = useState(false);
	const [showOrderComposer, setShowOrderComposer] = useState(false);
	const [selectedOrderPreview, setSelectedOrderPreview] = useState(null);
	const [highlightedRecordId, setHighlightedRecordId] = useState(null);
	const [workspaceView, setWorkspaceView] = useState('history');
	const [contractSearchQuery, setContractSearchQuery] = useState('');
	const [contractOrderDetailsById, setContractOrderDetailsById] = useState({});
	const [isLoadingContractOrders, setIsLoadingContractOrders] = useState(false);
	const [contractOrdersError, setContractOrdersError] = useState('');
	const bootstrapInFlight = useRef(false);

	const loadClientRecords = useCallback(async (clientId) => {
		if (!clientId) {
			setClientRecords([]);
			setClientRecordsError('');
			return [];
		}

		setIsLoadingClientRecords(true);
		setClientRecordsError('');

		try {
			const response = await api.get('/Orders', {
				params: {
					clientId,
					pageSize: 500,
					sortOrder: 'DESC',
					includeInvoices: true,
				},
			});
			const items = response.data?.items || response.data || [];
			setClientRecords(items);
			return items;
		} catch (error) {
			const message =
				error?.response?.data?.message ||
				error?.message ||
				'Unable to load client history.';
			setClientRecordsError(message);
			setClientRecords([]);
			return [];
		} finally {
			setIsLoadingClientRecords(false);
		}
	}, []);

	const bootstrapHub = useCallback(async () => {
		setIsBootstrapping(true);

			try {
				await Promise.allSettled([
					dispatch(fetchClients({ pageSize: 1000, page: 1, sortOrder: 'DESC' })),
					dispatch(
						fetchOrders({
							pageSize: 1000,
						currentPage: 1,
						sortOrder: 'DESC',
						includeInvoices: true,
					})
				),
			]);
		} finally {
			setIsBootstrapping(false);
		}
	}, [dispatch]);

	const refreshHubData = useCallback(
		async (clientIdToRefresh = selectedClientId) => {
			await Promise.allSettled([
				dispatch(fetchClients({ pageSize: 1000, page: 1, sortOrder: 'DESC' })),
				dispatch(
					fetchOrders({
						pageSize: 1000,
						currentPage: 1,
						sortOrder: 'DESC',
						includeInvoices: true,
					})
				),
				clientIdToRefresh
					? dispatch(fetchClientById(clientIdToRefresh))
					: Promise.resolve(),
				clientIdToRefresh ? loadClientRecords(clientIdToRefresh) : Promise.resolve(),
			]);
		},
		[dispatch, loadClientRecords, selectedClientId]
	);

	useEffect(() => {
		if (bootstrapInFlight.current) return;
		bootstrapInFlight.current = true;
		bootstrapHub().finally(() => {
			bootstrapInFlight.current = false;
		});
	}, [bootstrapHub]);

	useEffect(() => {
		if (!selectedClientId) {
			setClientRecords([]);
			return;
		}

		dispatch(fetchClientById(selectedClientId));
		loadClientRecords(selectedClientId);
	}, [dispatch, loadClientRecords, selectedClientId]);

	const clientOrdersIndex = useMemo(() => {
		return dashboardOrders.reduce((accumulator, order) => {
			if (!order?.clientId) {
				return accumulator;
			}

			if (!accumulator[order.clientId]) {
				accumulator[order.clientId] = [];
			}

			accumulator[order.clientId].push(order);
			return accumulator;
		}, {});
	}, [dashboardOrders]);

	const recentLeadQueue = useMemo(() => {
		return [...dashboardOrders]
			.filter((order) => Number(order?.status) === 1 && order?.clientId)
			.sort((left, right) => getRecordActivityTime(right) - getRecordActivityTime(left))
			.slice(0, 8);
	}, [dashboardOrders]);

	const clientCards = useMemo(() => {
		return [...clients]
			.map((client) => {
				const relatedOrders = clientOrdersIndex[client.id] || [];
				const leadCount = relatedOrders.filter(
					(order) => Number(order?.status) === 1
				).length;
				const activeOrderCount = relatedOrders.filter((order) => {
					const status = Number(order?.status);
					return status > 1 && status < 10;
				}).length;
				const totalOrderCount = relatedOrders.filter(
					(order) => Number(order?.status) !== 1
				).length;

				return {
					...client,
					leadCount,
					activeOrderCount,
					totalOrderCount,
					lastActivity: getClientActivityTime(client, relatedOrders),
				};
			})
			.sort((left, right) => right.lastActivity - left.lastActivity);
	}, [clientOrdersIndex, clients]);

	const filteredClientCards = useMemo(() => {
		const query = searchQuery.trim().toLowerCase();
		const queryPhone = removePhoneFormatting(searchQuery);

		return clientCards.filter((client) => {
			if (!query && !queryPhone) {
				return true;
			}

			const fullName = getClientDisplayName(client).toLowerCase();
			const companyName = (client?.companyName || '').toLowerCase();
			const email = (client?.email || '').toLowerCase();
			const clientPhone = removePhoneFormatting(client?.phoneNumber || '');

			return (
				fullName.includes(query) ||
				companyName.includes(query) ||
				email.includes(query) ||
				(queryPhone && clientPhone.includes(queryPhone))
			);
		});
	}, [clientCards, searchQuery]);

	useEffect(() => {
		if (selectedClientId || isBootstrapping) {
			return;
		}

		const fallbackClientId = recentLeadQueue[0]?.clientId || clientCards[0]?.id;
		if (fallbackClientId) {
			setSelectedClientId(fallbackClientId);
		}
	}, [clientCards, isBootstrapping, recentLeadQueue, selectedClientId]);

	useEffect(() => {
		if (!selectedClientId || isBootstrapping || clients.length === 0) {
			return;
		}

		const selectedStillExists = clients.some((client) => client.id === selectedClientId);
		if (selectedStillExists) {
			return;
		}

		setShowClientDetails(false);
		setSelectedOrderPreview(null);
		setSelectedClientId(recentLeadQueue[0]?.clientId || clientCards[0]?.id || null);
	}, [
		clientCards,
		clients,
		isBootstrapping,
		recentLeadQueue,
		selectedClientId,
	]);

	const activeClient =
		selectedClientDetails?.id === selectedClientId
			? selectedClientDetails
			: clientCards.find((client) => client.id === selectedClientId) || null;

	const visibleClientRecords = useMemo(() => {
		return [...clientRecords]
			.filter((record) => {
				const status = Number(record?.status);

				if (recordFilter === 'leads') {
					return status === 1;
				}

				if (recordFilter === 'orders') {
					return status !== 1;
				}

				if (recordFilter === 'open') {
					return status > 1 && status < 10;
				}

				if (recordFilter === 'done') {
					return status === 10;
				}

				return true;
			})
			.sort((left, right) => getRecordActivityTime(right) - getRecordActivityTime(left));
	}, [clientRecords, recordFilter]);

	const clientMetrics = useMemo(() => {
		return {
			leadCount: clientRecords.filter((record) => Number(record?.status) === 1).length,
			openOrderCount: clientRecords.filter((record) => {
				const status = Number(record?.status);
				return status > 1 && status < 10;
			}).length,
			doneOrderCount: clientRecords.filter(
				(record) => Number(record?.status) === 10
			).length,
			clientNoteCount: activeClient?.notes?.length || 0,
		};
	}, [activeClient?.notes?.length, clientRecords]);

	const contractCandidateOrderIds = useMemo(() => {
		return Array.from(
			new Set(
				[...dashboardOrders, ...clientRecords]
					.map((order) => order?.id)
					.filter(Boolean)
			)
		);
	}, [clientRecords, dashboardOrders]);

	const loadContractOrderDetails = useCallback(async (orderIds) => {
		if (!Array.isArray(orderIds) || orderIds.length === 0) {
			setContractOrdersError('');
			return;
		}

		setIsLoadingContractOrders(true);
		setContractOrdersError('');

		const nextOrderDetails = {};
		let failedCount = 0;
		const chunkSize = 20;

		try {
			for (let index = 0; index < orderIds.length; index += chunkSize) {
				const chunk = orderIds.slice(index, index + chunkSize);
				const results = await Promise.allSettled(
					chunk.map((orderId) => api.get(`/Orders/${orderId}`))
				);

				for (let resultIndex = 0; resultIndex < results.length; resultIndex += 1) {
					const orderId = chunk[resultIndex];
					const result = results[resultIndex];

					if (result.status === 'fulfilled') {
						nextOrderDetails[orderId] = result.value.data;
						continue;
					}

					failedCount += 1;
				}
			}

			if (Object.keys(nextOrderDetails).length > 0) {
				setContractOrderDetailsById((previousState) => ({
					...previousState,
					...nextOrderDetails,
				}));
			}

			if (failedCount > 0) {
				setContractOrdersError(
					`Some orders could not be loaded for contract follow-up (${failedCount}).`
				);
			}
		} catch (error) {
			setContractOrdersError(
				error?.response?.data?.message ||
					error?.message ||
					'Unable to load sent contracts.'
			);
		} finally {
			setIsLoadingContractOrders(false);
		}
	}, []);

	useEffect(() => {
		if (workspaceView !== 'contracts' || isBootstrapping) {
			return;
		}

		const missingOrderIds = contractCandidateOrderIds.filter(
			(orderId) => !contractOrderDetailsById[orderId]
		);

		if (missingOrderIds.length === 0) {
			return;
		}

		loadContractOrderDetails(missingOrderIds);
	}, [
		contractCandidateOrderIds,
		contractOrderDetailsById,
		isBootstrapping,
		loadContractOrderDetails,
		workspaceView,
	]);

	const allKnownOrders = useMemo(() => {
		const orderPool = [
			...dashboardOrders,
			...clientRecords,
			...Object.values(contractOrderDetailsById),
			...(currentOrder?.id ? [currentOrder] : []),
		];

		return Object.values(
			orderPool.reduce((accumulator, order) => {
				if (!order?.id) {
					return accumulator;
				}

				const previousOrder = accumulator[order.id] || {};
				const nextNotes =
					Array.isArray(order?.notes) && order.notes.length > 0
						? order.notes
						: previousOrder.notes || [];

				accumulator[order.id] = {
					...previousOrder,
					...order,
					notes: nextNotes,
				};
				return accumulator;
			}, {})
		);
	}, [clientRecords, contractOrderDetailsById, currentOrder, dashboardOrders]);

	const sentContracts = useMemo(() => {
		return allKnownOrders
			.map((order) => {
				const latestContractEvent = getLatestContractSentEvent(order?.notes);

				if (!latestContractEvent) {
					return null;
				}

				return {
					order,
					orderId: order.id,
					orderReference: order.reference || order.id?.slice(0, 8) || 'Unknown',
					clientId: order.clientId || order.client?.id || null,
					clientName:
						order.clientName || getClientDisplayName(order.client) || 'Unnamed client',
					clientCompanyName: order.companyName || order.client?.companyName || '',
					signerName:
						latestContractEvent.signerName ||
						order.clientName ||
						getClientDisplayName(order.client),
					signerEmail:
						latestContractEvent.signerEmail ||
						order.clientEmail ||
						order.client?.email ||
						'',
					sentAt: latestContractEvent.sentAt || latestContractEvent.createdAt,
					envelopeId: latestContractEvent.envelopeId,
				};
			})
			.filter(Boolean)
			.sort((left, right) => getTimeValue(right.sentAt) - getTimeValue(left.sentAt));
	}, [allKnownOrders]);

	const visibleSentContracts = useMemo(() => {
		const query = contractSearchQuery.trim().toLowerCase();

		if (!query) {
			return sentContracts;
		}

		return sentContracts.filter((contract) =>
			[
				contract.clientName,
				contract.clientCompanyName,
				contract.signerName,
				contract.signerEmail,
				String(contract.orderReference || ''),
				contract.envelopeId,
			].some((value) => (value || '').toLowerCase().includes(query))
		);
	}, [contractSearchQuery, sentContracts]);

	const topMetrics = useMemo(() => {
		return {
			clientCount: clients.length,
			leadCount: recentLeadQueue.length,
			activeClientCount: clientCards.filter((client) => client.activeOrderCount > 0)
				.length,
			selectedHistoryCount: clientRecords.length,
			sentContractCount: sentContracts.length,
		};
	}, [
		clientCards,
		clientRecords.length,
		clients.length,
		recentLeadQueue.length,
		sentContracts.length,
	]);

	const openClientFromLead = (order) => {
		setSelectedClientId(order.clientId);
		setHighlightedRecordId(order.id);
		setSelectedOrderPreview(null);
	};

	const openOrderDetails = (record) => {
		setSelectedOrderPreview(record);
		dispatch(fetchOrderById(record.id));
	};

	const openOrderFromContract = (contract) => {
		if (contract?.clientId) {
			setSelectedClientId(contract.clientId);
			dispatch(fetchClientById(contract.clientId));
		}

		setHighlightedRecordId(contract?.orderId || null);
		setShowClientDetails(false);
		openOrderDetails(contract.order);
	};

	const openClientDetailsFromContract = (contract) => {
		if (!contract?.clientId) {
			return;
		}

		setSelectedClientId(contract.clientId);
		dispatch(fetchClientById(contract.clientId));
		loadClientRecords(contract.clientId);
		setHighlightedRecordId(contract.orderId || null);
		setSelectedOrderPreview(null);
		setShowClientDetails(true);
	};

	const closeOrderDetails = async () => {
		setSelectedOrderPreview(null);
		await refreshHubData(selectedClientId);
	};

	const closeClientDetails = async () => {
		setShowClientDetails(false);
		await refreshHubData(selectedClientId);
	};

	const handleClientReady = async (clientId) => {
		setShowCreateClientPanel(false);
		setSelectedClientId(clientId);
		setHighlightedRecordId(null);
		await refreshHubData(clientId);
	};

	const handleOrderComposerClose = async () => {
		setShowOrderComposer(false);
		await refreshHubData(selectedClientId);
	};

	if (isBootstrapping) {
		return (
			<div className="flex flex-col h-screen">
				<Header />
				<div className="flex flex-1 overflow-hidden">
					<Sidebar />
					<main className="flex-1 overflow-y-auto bg-slate-50">
						<div className="client-management-page client-management-page--loading">
							<BncLogoSpinner size={90} />
						</div>
					</main>
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-col h-screen">
			<Header />
			<div className="flex flex-1 overflow-hidden">
				<Sidebar />
				<main className="flex-1 overflow-y-auto bg-slate-50">
					<div className="client-management-page">
						<section className="client-management-page__hero">
							<div>
								<p className="client-management-page__eyebrow">
									Dashboard Workspace
								</p>
								<h1>Client Management Hub</h1>
								<p>
									Manage the client, review every lead and order under that
									record, and jump into notes or status updates from one place.
								</p>
							</div>
							<div className="client-management-page__hero-actions">
								<button
									type="button"
									className="client-management-btn client-management-btn--secondary"
									onClick={() => setShowCreateClientPanel(true)}
								>
									<PlusSquare size={18} />
									Add New Client
								</button>
								<button
									type="button"
									className="client-management-btn client-management-btn--primary"
									onClick={() => setShowOrderComposer(true)}
									disabled={!selectedClientId}
								>
									<ArrowUpRight size={18} />
									New Lead / Order
								</button>
							</div>
						</section>

						<section className="client-management-page__metrics">
							<article className="client-management-card metric-card">
								<span>Clients Loaded</span>
								<strong>{topMetrics.clientCount}</strong>
								<p>Ready for search, duplicate checks, and quick selection.</p>
							</article>
							<article className="client-management-card metric-card">
								<span>Lead Queue</span>
								<strong>{topMetrics.leadCount}</strong>
								<p>Most recent quote records waiting for follow-up.</p>
							</article>
							<article className="client-management-card metric-card">
								<span>Clients With Active Orders</span>
								<strong>{topMetrics.activeClientCount}</strong>
								<p>Clients currently moving through the order pipeline.</p>
							</article>
							<article className="client-management-card metric-card">
								<span>Selected Client History</span>
								<strong>{topMetrics.selectedHistoryCount}</strong>
								<p>Lead and order records attached to the current client.</p>
							</article>
							<article className="client-management-card metric-card">
								<span>Sent Contracts</span>
								<strong>{topMetrics.sentContractCount}</strong>
								<p>Latest contract sends available for follow-up and drill-down.</p>
							</article>
						</section>

						<section className="client-management-layout">
							<div className="client-management-layout__sidebar">
								<div className="client-management-card">
									<div className="client-management-card__header">
										<div>
											<p className="client-management-card__eyebrow">
												Lead Queue
											</p>
											<h2>Recent Leads</h2>
										</div>
										<span className="client-management-pill">
											{recentLeadQueue.length} leads
										</span>
									</div>
									<div className="client-management-lead-list">
										{recentLeadQueue.length === 0 && (
											<div className="client-management-empty">
												<FileText size={20} />
												<p>No new leads were returned in the latest order batch.</p>
											</div>
										)}
										{recentLeadQueue.map((order) => (
											<button
												type="button"
												key={order.id}
												className={`client-management-lead-card ${
													highlightedRecordId === order.id ? 'is-highlighted' : ''
												}`}
												onClick={() => openClientFromLead(order)}
											>
												<div>
													<strong>{order.clientName || 'Client lead'}</strong>
													<span>
														Order #{order.reference || order.id?.slice(0, 8)}
													</span>
												</div>
												<div>
													<span>{new Date(order.createdAt).toLocaleDateString()}</span>
													<span>{getRecordStatusLabel(order.status)}</span>
												</div>
											</button>
										))}
									</div>
								</div>

								<div className="client-management-card client-management-card--stretch">
									<div className="client-management-card__header">
										<div>
											<p className="client-management-card__eyebrow">
												Client Search
											</p>
											<h2>Clients</h2>
										</div>
										<span className="client-management-pill">
											{filteredClientCards.length} shown
										</span>
									</div>

									<label className="client-management-search">
										<Search size={16} />
										<input
											type="text"
											value={searchQuery}
											onChange={(event) => setSearchQuery(event.target.value)}
											placeholder="Search by name, company, phone, or email"
										/>
									</label>

									<div className="client-management-client-list">
										{filteredClientCards.length === 0 && (
											<div className="client-management-empty">
												<Users size={20} />
												<p>No clients match that search yet.</p>
											</div>
										)}

										{filteredClientCards.map((client) => (
											<button
												type="button"
												key={client.id}
												className={`client-management-client-card ${
													selectedClientId === client.id ? 'is-selected' : ''
												}`}
												onClick={() => {
													setSelectedClientId(client.id);
													setHighlightedRecordId(null);
												}}
											>
												<div className="client-management-client-card__top">
													<div>
														<strong>{getClientDisplayName(client)}</strong>
														<span>{client.companyName || 'No company'}</span>
													</div>
													<span className="client-management-status-chip">
														{CLIENT_STATUS_LABELS[client.status] || 'Client'}
													</span>
												</div>
												<div className="client-management-client-card__meta">
													<span>{formatPhoneNumber(client.phoneNumber)}</span>
													<span>{client.email || 'No email'}</span>
												</div>
												<div className="client-management-client-card__counts">
													<span>{client.leadCount} leads</span>
													<span>{client.totalOrderCount} orders</span>
													<span>{client.activeOrderCount} active</span>
												</div>
											</button>
										))}
									</div>
								</div>
							</div>

							<div className="client-management-layout__workspace">
								{!activeClient && (
									<div className="client-management-card client-management-card--empty-workspace">
										<div className="client-management-empty">
											<Briefcase size={24} />
											<p>
												Select a client from the left to load their profile,
												lead list, and order history.
											</p>
										</div>
									</div>
								)}

								{activeClient && (
									<>
										<div className="client-management-card client-management-client-hero">
											<div className="client-management-client-hero__copy">
												<div className="client-management-card__header">
													<div>
														<p className="client-management-card__eyebrow">
															Selected Client
														</p>
														<h2>{getClientDisplayName(activeClient)}</h2>
													</div>
													<span className="client-management-status-chip">
														{CLIENT_STATUS_LABELS[activeClient.status] || 'Client'}
													</span>
												</div>
												<p>{activeClient.companyName || 'No company assigned'}</p>
												<div className="client-management-contact-row">
													<a href={`tel:${activeClient.phoneNumber || ''}`}>
														<Phone size={14} />
														{formatPhoneNumber(activeClient.phoneNumber) || 'No phone'}
													</a>
													{activeClient.email && (
														<a href={`mailto:${activeClient.email}`}>
															<Mail size={14} />
															{activeClient.email}
														</a>
													)}
												</div>
												<p className="client-management-address">
													{getClientAddressLine(activeClient) || 'Address not added yet'}
												</p>
											</div>

											<div className="client-management-client-hero__actions">
												<button
													type="button"
													className="client-management-btn client-management-btn--secondary"
													onClick={() => setShowClientDetails(true)}
												>
													Manage Client / Notes
												</button>
												<a
													className="client-management-btn client-management-btn--secondary"
													href="https://design.bestnationalcarports.com/"
													target="_blank"
													rel="noopener noreferrer"
												>
													<ArrowUpRight size={18} />
													Start Quote in 3D Design
												</a>
												<button
													type="button"
													className="client-management-btn client-management-btn--primary"
													onClick={() => setShowOrderComposer(true)}
												>
													Create Lead / Order
												</button>
											</div>
										</div>

										<div className="client-management-page__metrics client-management-page__metrics--compact">
											<article className="client-management-card metric-card metric-card--compact">
												<span>Leads</span>
												<strong>{clientMetrics.leadCount}</strong>
											</article>
											<article className="client-management-card metric-card metric-card--compact">
												<span>Open Orders</span>
												<strong>{clientMetrics.openOrderCount}</strong>
											</article>
											<article className="client-management-card metric-card metric-card--compact">
												<span>Done</span>
												<strong>{clientMetrics.doneOrderCount}</strong>
											</article>
											<article className="client-management-card metric-card metric-card--compact">
												<span>Client Notes</span>
												<strong>{clientMetrics.clientNoteCount}</strong>
											</article>
										</div>

										<div className="client-management-card client-management-workspace-card">
											<div className="client-management-card__header">
												<div>
													<p className="client-management-card__eyebrow">
														Follow Up Workspace
													</p>
													<h2>History and Contracts</h2>
												</div>
												<div className="client-management-filter-pills">
													{[
														{ id: 'history', label: 'Client History' },
														{ id: 'contracts', label: 'Sent Contracts' },
													].map((view) => (
														<button
															type="button"
															key={view.id}
															className={
																workspaceView === view.id ? 'is-active' : ''
															}
															onClick={() => setWorkspaceView(view.id)}
														>
															{view.label}
														</button>
													))}
												</div>
											</div>
											<p className="client-management-workspace-card__copy">
												{workspaceView === 'history'
													? 'Review the selected client history, filter records, and open the full order drawer.'
													: 'Track the latest contract sent for each order, count follow-ups, and jump straight to the client record.'}
											</p>
										</div>

										{workspaceView === 'history' ? (
											<div className="client-management-card">
												<div className="client-management-card__header">
													<div>
														<p className="client-management-card__eyebrow">
															Client History
														</p>
														<h2>Leads and Orders</h2>
													</div>
													<div className="client-management-filter-pills">
														{[
															{ id: 'all', label: 'All' },
															{ id: 'leads', label: 'Leads' },
															{ id: 'orders', label: 'Orders' },
															{ id: 'open', label: 'Open' },
															{ id: 'done', label: 'Done' },
														].map((filter) => (
															<button
																type="button"
																key={filter.id}
																className={
																	recordFilter === filter.id ? 'is-active' : ''
																}
																onClick={() => setRecordFilter(filter.id)}
															>
																{filter.label}
															</button>
														))}
													</div>
												</div>

												{clientRecordsError && (
													<div className="client-management-inline-error">
														{clientRecordsError}
													</div>
												)}

												{isLoadingClientRecords ? (
													<div className="client-management-history-loading">
														<BncLogoSpinner size={55} />
													</div>
												) : (
													<div className="client-management-record-list">
														{visibleClientRecords.length === 0 && (
															<div className="client-management-empty">
																<FileText size={20} />
																<p>
																	This client does not have records in the selected
																	filter yet.
																</p>
															</div>
														)}

														{visibleClientRecords.map((record) => (
															<article
																key={record.id}
																className={`client-management-record-card ${
																	highlightedRecordId === record.id
																		? 'is-highlighted'
																		: ''
																}`}
															>
																<div className="client-management-record-card__top">
																	<div>
																		<span className="client-management-record-card__type">
																			{getRecordType(record)}
																		</span>
																		<h3>
																			Order #{record.reference || record.id?.slice(0, 8)}
																		</h3>
																	</div>
																	<span className="client-management-status-chip">
																		{getRecordStatusLabel(record.status)}
																	</span>
																</div>
																<div className="client-management-record-card__meta">
																	<span>
																		Created{' '}
																		{new Date(record.createdAt).toLocaleDateString()}
																	</span>
																	<span>
																		Last update{' '}
																		{new Date(
																			record.updatedAt || record.createdAt
																		).toLocaleDateString()}
																	</span>
																	<span>
																		{currencyFormatter.format(getRecordAmount(record))}
																	</span>
																	<span>
																		{record?.notes?.length || 0} order notes
																	</span>
																</div>
																<div className="client-management-record-card__actions">
																	<button
																		type="button"
																		className="client-management-btn client-management-btn--secondary"
																		onClick={() => openOrderDetails(record)}
																	>
																		Open Record
																	</button>
																</div>
															</article>
														))}
													</div>
												)}
											</div>
										) : (
											<div className="client-management-card">
												<div className="client-management-card__header">
													<div>
														<p className="client-management-card__eyebrow">
															Contract Follow Up
														</p>
														<h2>Latest Sent Contracts</h2>
													</div>
													<span className="client-management-pill">
														{visibleSentContracts.length} sent
													</span>
												</div>

												<label className="client-management-search client-management-search--compact">
													<Search size={16} />
													<input
														type="text"
														value={contractSearchQuery}
														onChange={(event) =>
															setContractSearchQuery(event.target.value)
														}
														placeholder="Search by client, email, order, or envelope"
													/>
												</label>

												{contractOrdersError && (
													<div className="client-management-inline-error">
														{contractOrdersError}
													</div>
												)}

												{isLoadingContractOrders && visibleSentContracts.length === 0 ? (
													<div className="client-management-history-loading">
														<BncLogoSpinner size={55} />
													</div>
												) : (
												<div className="client-management-record-list">
													{visibleSentContracts.length === 0 && (
														<div className="client-management-empty">
															<FileText size={20} />
															<p>
																No sent contracts have been tracked yet. Once a
																contract is sent from an order, it will show up here.
															</p>
														</div>
													)}

													{visibleSentContracts.map((contract) => (
														<article
															key={contract.orderId}
															className={`client-management-record-card client-management-contract-card ${
																highlightedRecordId === contract.orderId
																	? 'is-highlighted'
																	: ''
															}`}
														>
															<div className="client-management-record-card__top">
																<div>
																	<span className="client-management-record-card__type">
																		Sent Contract
																	</span>
																	<h3>Order #{contract.orderReference}</h3>
																</div>
																<span className="client-management-status-chip">
																	Latest send
																</span>
															</div>
															<div className="client-management-contract-card__summary">
																<strong>{contract.clientName}</strong>
																<span>
																	{contract.clientCompanyName || 'No company'}
																</span>
															</div>
															<div className="client-management-record-card__meta client-management-record-card__meta--contracts">
																<span>
																	Sent to {contract.signerName || contract.clientName}
																</span>
																<span>{contract.signerEmail || 'No email on file'}</span>
																<span>Sent {formatDateTime(contract.sentAt)}</span>
																<span className="client-management-contract-card__envelope">
																	Envelope {contract.envelopeId}
																</span>
															</div>
															<div className="client-management-record-card__actions">
																<button
																	type="button"
																	className="client-management-btn client-management-btn--secondary"
																	onClick={() =>
																		openClientDetailsFromContract(contract)
																	}
																	disabled={!contract.clientId}
																>
																	Open Client Info
																</button>
																<button
																	type="button"
																	className="client-management-btn client-management-btn--primary"
																	onClick={() => openOrderFromContract(contract)}
																>
																	Open Order
																</button>
															</div>
														</article>
													))}
												</div>
												)}
											</div>
										)}
									</>
								)}
							</div>
						</section>
					</div>
				</main>
			</div>

			{showCreateClientPanel && (
				<div className="client-hub-overlay">
					<div className="client-hub-overlay__backdrop" />
					<div className="client-hub-overlay__panel client-hub-overlay__panel--sheet">
						<ClientHubCreateClientPanel
							clients={clients}
							currentUserId={currentUser?.id}
							onCancel={() => setShowCreateClientPanel(false)}
							onClientReady={handleClientReady}
						/>
					</div>
				</div>
			)}

			{showOrderComposer && selectedClientId && (
				<div className="client-hub-overlay client-hub-overlay--composer">
					<div className="client-hub-overlay__backdrop" />
					<div className="client-hub-overlay__panel client-hub-overlay__panel--composer">
						<ClientHubOrderComposer
							clientId={selectedClientId}
							onClose={handleOrderComposerClose}
						/>
					</div>
				</div>
			)}

			{showClientDetails && selectedClientId && (
				<ClientDetails
					selectedClient={selectedClientId}
					onClose={closeClientDetails}
					closeSlide={closeClientDetails}
				/>
			)}

			{selectedOrderPreview && (
				<OrderDetails order={selectedOrderPreview || currentOrder} onClose={closeOrderDetails} />
			)}
		</div>
	);
}
